<template>
  <div class="sub-accounts-container">
    <!-- Control Bar -->
    <SubAccountsControlBar
      :start-date="startDate"
      :end-date="endDate"
      :selected-account-ids="selectedAccountIds"
      :available-accounts="availableAccounts"
      :selected-period="selectedPeriod"
      @change="handleFilterChange"
      @update:period="handlePeriodChange"
    />

    <!-- Loading State -->
    <div v-if="loadingAccounts" class="loading-accounts">
      <div class="loading-spinner"></div>
      <span>Loading accounts...</span>
    </div>

    <!-- No Accounts State -->
    <div v-if="!loadingAccounts && availableAccounts.length === 0" class="no-accounts">
      <span>No sub-accounts available</span>
    </div>

    <!-- Summary Cards -->
    <div class="summary-section">
      <!-- Total Loads Widget -->
      <div class="widget-container">
        <SummarySection
          v-if="data.totalSummary"
          title="Total Loads"
          :value="(data.totalSummary && data.totalSummary.totalLoads && data.totalSummary.totalLoads.value) || 0"
          :variation="(data.totalSummary && data.totalSummary.totalLoads && data.totalSummary.totalLoads.variation) || 0"
          icon="fas fa-truck"
          icon-color="#4F46E5"
        />
        <div v-else class="spinner-container">
          <ProgressSpinner />
        </div>
      </div>

      <!-- Total Revenue Widget -->
      <div class="widget-container">
        <SummarySection
          v-if="data.totalSummary"
          title="Total Revenue"
          :value="(data.totalSummary && data.totalSummary.totalRevenue && data.totalSummary.totalRevenue.value) || 0"
          :variation="(data.totalSummary && data.totalSummary.totalRevenue && data.totalSummary.totalRevenue.variation) || 0"
          :is-currency="true"
          icon="fas fa-dollar-sign"
          icon-color="#10B981"
        />
        <div v-else class="spinner-container">
          <ProgressSpinner />
        </div>
      </div>

      <!-- Active Accounts Widget -->
      <div class="widget-container">
        <SummarySection
          v-if="data.activeAccounts"
          title="Active Accounts"
          :value="(data.activeAccounts && data.activeAccounts.accounts_with_loads) || 0"
          :variation="0"
          icon="fas fa-users"
          icon-color="#8B5CF6"
        />
        <div v-else class="spinner-container">
          <ProgressSpinner />
        </div>
      </div>

      <!-- Total Accounts Widget -->
      <div class="widget-container">
        <SummarySection
          v-if="data.totalAccounts"
          title="Total Accounts"
          :value="(data.totalAccounts && data.totalAccounts.total_accounts) || 0"
          :variation="0"
          icon="fas fa-building"
          icon-color="#F59E0B"
        />
        <div v-else class="spinner-container">
          <ProgressSpinner />
        </div>
      </div>
    </div>

    <!-- Loads by Status -->
    <div class="widget-container status-widget">
      <StatusSection
        v-if="data.loadsQuoted && data.loadsAssigned && data.loadsInTransit && data.loadsDelivered"
        :loads-quoted="data.loadsQuoted"
        :loads-assigned="data.loadsAssigned"
        :loads-in-transit="data.loadsInTransit"
        :loads-delivered="data.loadsDelivered"
        :processed-status-data="processedStatusData"
      />
      <div v-else class="spinner-container">
        <ProgressSpinner />
      </div>
    </div>

    <!-- Charts Section -->
    <div class="widget-container charts-widget">
      <ChartsSection
        v-if="data.loadsByAccount && data.revenuePerMonth && data.topAccountsByLoads && data.topAccountsByRevenue"
        :formatted-loads-by-account="formattedLoadsByAccount"
        :revenue-legend-items="revenueLegendItems"
        :revenue-per-month="data.revenuePerMonth"
        :revenue-per-month-option="revenueChartOption"
        :formatted-top-accounts-loads="formattedTopAccountsLoads"
        :formatted-top-accounts-revenue="formattedTopAccountsRevenue"
        :loads-by-account-data="(data.loadsByAccount && data.loadsByAccount.data) || []"
        :top-accounts-loads="(data.topAccountsByLoads) || []"
        :top-accounts-revenue="(data.topAccountsByRevenue) || []"
      />
      <div v-else class="spinner-container">
        <ProgressSpinner />
      </div>
    </div>
  </div>
</template>

<script>
import ls from "@/plugins/secureLs";
import useDashboard from "@/composables/useDashboard";
import SubAccountsData from "@/mixins/SubAccountsData";
import DecodeToken from "@/utils/DecodeToken";
import { errorMessage } from "@/utils/ErrorMessages";
import ProgressSpinner from 'primevue/progressspinner';
import { createRevenuePerMonthOption } from "./config/chartOptions";
import SubAccountsControlBar from "./components/SubAccountsControlBar.vue";
import SummarySection from "./components/SummarySection.vue";
import StatusSection from "./components/StatusSection.vue";
import ChartsSection from "./components/ChartsSection.vue";

export default {
  name: "SubAccounts",
  components: {
    SubAccountsControlBar,
    SummarySection,
    StatusSection,
    ChartsSection,
    ProgressSpinner,
  },
  mixins: [SubAccountsData],
  setup() {
    const {
      data,
      loading,
      error,
      fetchDashboard,
    } = useDashboard();

    return {
      data,
      loading,
      error,
      fetchDashboard,
    };
  },
  data() {
    const { startDate, endDate } = this.getDefaultDateRange();

    return {
      startDate,
      endDate,
      selectedAccountIds: [],
      availableAccounts: [],
      loadingAccounts: false,
      selectedPeriod: 'last-6-months',
      accountColors: [
        '#4F46E5', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444',
        '#EC4899', '#14B8A6', '#F97316', '#06B6D4', '#84CC16'
      ]
    };
  },
  mounted() {
    this.loadAvailableAccounts();
  },
  methods: {
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    getDefaultDateRange() {
      const end = new Date();
      const start = new Date();
      start.setMonth(start.getMonth() - 6);

      return {
        startDate: this.formatDate(start),
        endDate: this.formatDate(end),
      };
    },

    async loadAvailableAccounts() {
      this.loadingAccounts = true;
      try {
        const mainAccountId = this.getMainAccountIdFromToken();
        if (!mainAccountId) return;

        const rawAccounts = await this.fetchChildrenAccounts(mainAccountId);
        this.availableAccounts = this.formatAccounts(rawAccounts);

        if (this.availableAccounts.length > 0) {
          this.setInitialSelection();
          this.initConnection();
        }
      } catch (error) {
        this.handleAccountError(error);
      } finally {
        this.loadingAccounts = false;
      }
    },

    getMainAccountIdFromToken() {
      try {
        const token = ls.get("portal-front-token");
        if (!token) {
          errorMessage("No authentication token found");
          return null;
        }
        const decodedToken = DecodeToken(token);
        const subData = typeof decodedToken.sub === 'string'
          ? JSON.parse(decodedToken.sub)
          : decodedToken.sub;
        const subAccounts = subData?.accounts || [];
        if (subAccounts.length === 0) {
          errorMessage("No accounts found in authentication token");
          return null;
        }
        const mainAccountId = subData.account_id;
        return mainAccountId;
      } catch (error) {
        errorMessage("Error extracting account information from token");
        return null;
      }
    },

    async fetchChildrenAccounts(accountId) {
      try {
        const childrenAccounts = await this.$store.dispatch(
          'accountChildren/getAllChildrenAccounts',
          { account_id: accountId }
        );
        return childrenAccounts;
      } catch (error) {
        return null;
      }
    },

    formatAccounts(accounts) {
      const formattedAccounts = accounts.map((account, index) => ({
        id: account.children_id,
        name: account.children_name,
        color: this.accountColors[index % this.accountColors.length]
      }));
      return formattedAccounts;
    },

    setInitialSelection() {
      const maxSelection = Math.min(this.availableAccounts.length, 4);
      this.selectedAccountIds = this.availableAccounts
        .slice(0, maxSelection)
        .map((account) => account.id);
    },

    handleAccountError() {
      errorMessage("Error loading available accounts");
      this.availableAccounts = [];
      this.selectedAccountIds = [];
    },
    initConnection() {
      const params = {
        token: ls.get("portal-front-token"),
        startDate: this.startDate,
        endDate: this.endDate,
        childAccountIds: this.selectedAccountIds,
      };

      this.fetchDashboard(params);
    },
    handleFilterChange(filters) {
      this.startDate = filters.startDate;
      this.endDate = filters.endDate;
      this.selectedAccountIds = filters.selectedAccountIds;

      const params = {
        token: ls.get("portal-front-token"),
        startDate: this.startDate,
        endDate: this.endDate,
        childAccountIds: this.selectedAccountIds,
      };

      this.fetchDashboard(params);
    },
    handlePeriodChange(newPeriod) {
      this.selectedPeriod = newPeriod;
    },
  },
  computed: {
    revenueChartOption() {
      const revenueData = this.data?.revenuePerMonth;
      if (!revenueData || !revenueData.months || !revenueData.series) {
        return {};
      }
      const option = createRevenuePerMonthOption(revenueData, this.accountColors, this.formatCurrency);
      return option;
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@/assets/scss/_themes" as *;
@use "@/assets/scss/_variables" as *;
@use "@/assets/scss/_dashboard-mixins" as *;
@use "@/assets/scss/_mixins" as *;

.loading-accounts, .no-accounts {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  margin-bottom: 24px;
  background: white;
  border: 1px solid $color-gray-divider;
  border-radius: 8px;
  color: $color-gray-text-dark;
  font-size: 14px;
}

.loading-accounts {
  .loading-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid $color-gray-divider;
    border-top: 2px solid $color-navy-medium;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-right: 8px;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.sub-accounts-container {
  padding: 20px;
  font-family: $font-family-portal;

  .widget-container {
    min-height: 120px;
    position: relative;
  }

  .status-widget {
    min-height: 300px;
  }

  .charts-widget {
    min-height: 600px;
  }

  .spinner-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: inherit;
    background: transparent;

    :deep(.p-progress-spinner) {
      width: 40px;
      height: 40px;
    }

    :deep(.p-progress-spinner-circle) {
      stroke: $color-navy-light !important;
      animation: p-progress-spinner-dash 1.5s ease-in-out infinite;
    }
  }

  .summary-section {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 20px;
    margin-bottom: 30px;
  }

  .status-section {
    margin-bottom: 30px;

    .status-cards {
      @include status-grid;

      .status-card {
        @include card-base;
        overflow: visible !important;
        &.status-quoted {
          @include status-accent($color-status-quoted);
        }

        &.status-assigned {
          @include status-accent($color-status-assigned);
        }

        &.status-in_transit {
          @include status-accent($color-status-in-transit);
        }

        &.status-delivered {
          @include status-accent($color-status-delivered);
        }

        .status-header {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          margin-bottom: 20px;

          .status-total {
            font-size: 24px;
            font-weight: bold;
            color: #001737;
          }
        }

        .account-list {
          display: flex;
          flex-direction: column;
          gap: 12px;

          .account-item {
            display: grid;
            grid-template-columns: 140px 1fr 50px;
            align-items: center;
            gap: 12px;

            .account-name {
              font-size: 13px;
              color: #6b7280;
              font-weight: 500;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }

            .progress-bar-wrapper {
              @include progress-bar-container;

              .progress-bar {
                height: 100%;
                border-radius: 4px;
                transition: width 0.3s ease;
                min-width: 4px;
                position: absolute;
                left: 0;
                top: 0;
              }

              .account-value {
                font-size: 14px;
                font-weight: bold;
                color: #001737;
                text-align: right;
              }
            }
          }
        }
      }
    }
  }
}

.charts-grid {
  display: grid !important;
  grid-template-columns: repeat(2, 1fr) !important;
  gap: 24px !important;
  width: 100% !important;
  height: auto !important;

  .section-divider {
    grid-column: span 2;
    height: 1px;
    background-color: #e5e7eb;
    width: 100%;
    margin: 30px 0;
  }

  .active-accounts-widget {
    grid-column: span 2;
    display: flex;
    justify-content: flex-start;
    margin-bottom: 20px;

    .summary-card {
      max-width: 400px;
      width: 50%;
    }
  }

  .chart-card {
    @include chart-card;

    h3 {
      margin-bottom: 20px;
      color: #1f2937;
      font-size: 16px;
    }

    .chart {
      height: 320px;
      width: 100%;
      :deep(.echarts-tooltip) {
        color: #1f2937 !important;
      }
      :deep(.echarts-legend) {
        color: #374151 !important;
      }
      :deep(.echarts-axis-label) {
        color: #6b7280 !important;
      }
      :deep(.echarts-axis-line) {
        color: #e5e7eb !important;
      }
    }
    .revenue-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      padding: 0;

      .revenue-title {
        margin: 0;
        color: #1f2937;
        font-size: 18px;
        font-weight: 600;
      }

      .revenue-legend {
        display: flex;
        align-items: center;
        gap: 20px;
        flex-wrap: wrap;

        .legend-item {
          display: flex;
          align-items: center;
          gap: 8px;

          .legend-color-line {
            width: 16px;
            height: 4px;
            border-radius: 2px;
            flex-shrink: 0;
          }

          .legend-text {
            font-size: 14px;
            color: #6b7280;
            font-weight: 500;
            white-space: nowrap;
          }
        }

        .legend-more {
          font-size: 14px;
          color: #9ca3af;
          font-weight: 500;
        }
      }
    }
    .top-accounts-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      padding: 0;

      .top-accounts-title {
        margin: 0;
        color: #1f2937;
        font-size: 18px;
        font-weight: 600;
      }

      .top-accounts-label {
        font-size: 12px;
        color: #9ca3af;
        font-weight: 500;
        letter-spacing: 0.5px;
        text-transform: uppercase;
      }
    }
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .sub-accounts-container {
    padding: 16px;
    overflow-x: hidden;
    width: 100%;
    max-width: 100% !important;
    box-sizing: border-box;

    .summary-section {
      grid-template-columns: 1fr;
      gap: 20px;
      margin-bottom: 24px;
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box;
    }

    .status-section {
      margin-bottom: 24px;
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box;

      .status-cards {
        display: flex;
        flex-direction: column;
        width: 100% !important;
        max-width: 100% !important;
        gap: 16px;
        box-sizing: border-box;

        .status-card {
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          box-sizing: border-box;
          margin-bottom: 0;

          .status-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 16px;

            .status-total {
              font-size: 20px;
            }
          }

          .account-list {
            gap: 10px;

            .account-item {
              grid-template-columns: 120px 1fr 45px;
              gap: 8px;

              .account-name {
                font-size: 12px;
              }

              .account-value {
                font-size: 12px;
              }
            }
          }
        }
      }
    }
  }

  .charts-grid {
    display: flex !important;
    flex-direction: column !important;
    grid-template-columns: 1fr !important;
    gap: 16px !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    box-sizing: border-box;

    .section-divider {
      grid-column: span 1 !important;
      margin: 20px 0;
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box;
    }

    .active-accounts-widget {
      grid-column: span 1 !important;
      margin-bottom: 16px;
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      box-sizing: border-box;

      .summary-card {
        max-width: 100% !important;
        width: 100% !important;
        min-width: 0 !important;
        box-sizing: border-box;
      }
    }

    .chart-card {
      height: auto !important;
      min-height: 500px;
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      box-sizing: border-box;

      .chart {
        height: 400px !important;
        min-height: 400px;
        width: 100% !important;
        max-width: 100% !important;
        overflow: hidden;
        box-sizing: border-box;
      }

      .revenue-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box;

        .revenue-legend {
          gap: 12px;
          max-width: 100%;
          overflow-x: auto;
          padding-bottom: 4px;

          .legend-item {
            .legend-text {
              font-size: 12px;
            }
          }

          .legend-more {
            font-size: 12px;
          }
        }
      }

      .top-accounts-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box;

        .top-accounts-title {
          font-size: 16px;
        }

        .top-accounts-label {
          font-size: 11px;
        }
      }
    }
  }
}

@media (min-width: 769px) and (max-width: 1023px) {
  .sub-accounts-container {
    padding: 18px;

    .summary-section {
      grid-template-columns: 1fr;
      gap: 20px;
      margin-bottom: 28px;
    }
  }

  .charts-grid {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 16px !important;

    .section-divider {
      grid-column: span 2 !important;
    }

    .active-accounts-widget {
      grid-column: span 2 !important;

      .summary-card {
        max-width: 100% !important;
        width: 100% !important;
      }
    }

    .chart-card {
      .revenue-header {
        .revenue-legend {
          gap: 16px;
          flex-wrap: wrap;

          .legend-text {
            font-size: 13px;
          }
        }
      }
    }
  }
}

@media (min-width: 1024px) and (max-width: 1279px) {
  .charts-grid {
    gap: 20px !important;

    .active-accounts-widget {
      .summary-card {
        max-width: 100% !important;
        width: 100% !important;
      }
    }
  }
}

</style>
